import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import SheepDetail from './SheepDetail.tsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
/* eslint-disable no-unused-vars -- shell primitives are used in JSX only */
import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageNotFound,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
/* eslint-enable no-unused-vars */
// eslint-disable-next-line no-unused-vars -- JSX-only use
import InlineNotice from '../shared/InlineNotice.jsx';
import RecordPageLoadError from '../shared/RecordPageLoadError.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {SheepAnimalPatchVars, SheepLambingCreateVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import {softDeleteSheepAnimal} from '../lib/sheepDeleteApi.js';
import {transferSheepAnimal} from '../lib/animalTransferApi.js';
import {canWriteLivestockRecords, canWriteLivestockTables, type AuthState as AccessAuthState} from '../lib/access.js';
import {deleteSheepLambingRecord} from '../lib/sheepLambingApi.js';
import {reportSaving, reportSaved, reportSaveFailed} from '../lib/saveStatus.js';
import {formatRuminantAge} from '../cattle/ruminantPresentation.js';
import Header from '../shared/Header.tsx';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import type {RecordSeqEntry, RecordSeqId} from '../lib/recordSequence.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {ISODateInput} from '../lib/dateUtils.js';

// The resource error is only ever read for `.message`; the catalog types it
// `unknown` because a query function may throw anything.
type ErrorLike = {message?: string};

// The catalog rethrows PostgREST's `error.message` unchanged, so reading
// `.message` off the rejection reproduces the notice text the `{error}`
// envelope produced — including the "undefined" a message-less error would have
// rendered before. This is a conversion, not a rewording.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as ErrorLike).message) : String(err);

// Row shapes. Every read below is `select('*')`, so each row is modelled as an
// open record with ONLY the columns this page actually names declared — the
// same decision routeDataCatalog.ts records for the same reason. Nothing here
// invents a schema TypeScript cannot verify against the SQL.
type SheepOldTagEntry = {tag?: string | null; source?: string | null};
type SheepAnimalRow = {
  [column: string]: unknown;
  id: string;
  tag: string | null;
  flock: string | null;
  birth_date: string | null;
  dam_tag: string | null;
  old_tags?: readonly SheepOldTagEntry[] | null;
};
type SheepWeighInRow = {[column: string]: unknown; tag: string | null};
type SheepLambingRow = {[column: string]: unknown; dam_tag: string | null};
type SheepOptionRow = {[column: string]: unknown};

// The catalog's payload type is per-column `unknown` (see above); this states
// which of those columns the record page reads. It is the one narrowing at the
// resource boundary rather than a cast at each of the forty read sites.
type SheepAnimalDetail = {
  animal: SheepAnimalRow | null;
  sheep: SheepAnimalRow[];
  weighIns: SheepWeighInRow[];
  lambingRecords: SheepLambingRow[];
  breedOptions: SheepOptionRow[];
  originOptions: SheepOptionRow[];
};
type SheepAnimalResource = Omit<ReturnType<typeof useRouteResource>, 'data' | 'error'> & {
  data: SheepAnimalDetail | null;
  error: ErrorLike | null;
};

// Route state written by the flock list rows and by sheep-to-sheep navigation
// below. `location.state` is `unknown` at the navigation seam by design, so the
// shape is declared where it is read.
type SheepAnimalRouteState = {
  fromSheepId?: string | null;
  fromSheepTag?: string | null;
  recordSeq?: RecordSeqEntry[] | null;
};

// What SheepDetail's inline editor hands to onPatch: the edited columns only.
// `tag` is named because the activity label reads it; the rest stay unknown.
type SheepPatchFields = {[column: string]: unknown; tag?: string | null};

// SheepDetail's lambing form state, verbatim: every control is a text input or
// a checkbox, so the numeric columns arrive as strings and are parsed below.
type LambingFormData = {
  lambing_date: string;
  total_born: string;
  deaths: string;
  complications_flag: boolean;
  complications_desc: string;
  notes: string;
};

// `authState` arrives from SheepFlocksRouter's useAuth() read, which the sheep
// and cattle routers alike narrow to `AuthIdentity | null` — the signed-out
// `false` member of AuthState never reaches a record route.
type SheepAnimalPageProps = {
  sb: SupabaseClient;
  fmt: (iso: ISODateInput) => string;
  authState: AuthIdentity | null;
};

const FLOCK_LABELS: Record<string, string> = {
  rams: 'Rams',
  ewes: 'Ewes',
  feeders: 'Feeders',
  sold: 'Sold',
  deceased: 'Deceased',
  processed: 'Processed',
};
const ALL_FLOCKS = ['rams', 'ewes', 'feeders', 'processed', 'deceased', 'sold'];

function age(birth: string | null) {
  return formatRuminantAge(birth, 'mo');
}

export default function SheepAnimalPage({sb, fmt, authState}: SheepAnimalPageProps) {
  const navigate = useNavigate();
  // `location.state` is `unknown` by design (src/app/navigation/appLocation.ts):
  // the write side normalized it and no consumer may read it without proving its
  // shape. The route state this page reads is exactly the one the flock list and
  // recordSeqNavOptions write, so that proof is applied ONCE, here at the hook
  // call, instead of at the three reads — which leaves every read the same
  // expression it has always been. The location shape is taken from the facade
  // hook itself, so no facade internal is imported.
  const location = useLocation() as Omit<ReturnType<typeof useLocation>, 'state'> & {
    state?: SheepAnimalRouteState | null;
  };
  const sheepId = location.pathname.replace('/sheep/flocks/', '');
  const fromSheepId = location.state?.fromSheepId || null;
  const fromSheepTag = location.state?.fromSheepTag || null;
  // Originating list order (visible rows) handed through route state. Absent on
  // direct links, notifications, and sheep-to-sheep click-throughs.
  const recordSeq = location.state?.recordSeq || null;

  // B3.3: the record page's six reads come from a key-scoped resource. A -> B
  // navigation is a key change, so a late response for A cannot paint into B.
  const record = useRouteResource(
    RESOURCE.SHEEP_ANIMAL_DETAIL,
    React.useMemo(() => ({sheepId}), [sheepId]),
  ) as SheepAnimalResource;
  const animal = record.data ? record.data.animal : null;
  const allSheep = record.data ? record.data.sheep : [];
  const weighIns = record.data ? record.data.weighIns : [];
  const lambingRecs = record.data ? record.data.lambingRecords : [];
  const breedOpts = record.data ? record.data.breedOptions : [];
  const originOpts = record.data ? record.data.originOptions : [];
  const loading = record.loading;
  const loadAll = record.refetch;
  // The record page's two declared writes, each handed the `sb` this surface was
  // given rather than the module singleton. The lambing insert has two readers
  // rather than cattle's six — sheep has no breeding page and no forecast — and
  // the entry says so instead of leaving a reader to infer it from the call
  // site. The animal patch has TEN, which is the whole reason it moved.
  const createLambing = useRouteMutation<SheepLambingCreateVars & {damSheepId?: string}, null>(
    MUTATION.SHEEP_LAMBING_CREATE,
    {client: sb},
  );
  const patchSheepAnimal = useRouteMutation<SheepAnimalPatchVars & {expected?: Record<string, unknown>}, null>(
    MUTATION.SHEEP_ANIMAL_PATCH,
    {client: sb},
  );
  const loadError: Notice | null = record.error
    ? {
        kind: 'error',
        message:
          'Could not load sheep record. Please refresh the page. (' + (record.error.message || record.error) + ')',
      }
    : null;
  const [notice, setNotice] = React.useState<Notice | null>(null);

  React.useEffect(() => {
    if (!loading && location.hash) {
      const anchor = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 200);
    }
  }, [loading, location.hash]);

  async function patchSheep(fields: SheepPatchFields | null | undefined) {
    if (!animal || !fields) return;
    setNotice(null);
    const expected: Record<string, unknown> = {};
    for (const key of Object.keys(fields)) {
      expected[key] = animal[key] ?? null;
    }
    const saveKey = `sheep-animal:${animal.id}:${Object.keys(fields).sort().join(',')}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      await patchSheepAnimal.run({sheepId: animal.id, expected, patch: fields});
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      // Byte-identical to what `onError(msg)` rendered: the seam rethrows
      // PostgREST's own `error.message` and `writeErrorMessage` reads it back,
      // which is exactly the string the `{error}` envelope handed `msg`.
      const msg = 'Save failed: ' + writeErrorMessage(err);
      setNotice({kind: 'error', message: msg});
      reportSaveFailed(saveKey, () => void patchSheep(fields), saveAttempt);
      return;
    }
    // Migration 244: public.sheep_animal_operation writes the field.changed
    // Activity row and receipt in the same transaction as the sheep row update.
    // The trailing `loadAll()` is gone, and losing it is the point rather than a
    // side effect: it refreshed ONE of the ten resources that read `sheep`.
    // `sheep.animal.patch` invalidates all ten, so this page still refetches —
    // it is declared instead of remembered, and it now reaches the nine
    // surfaces this call site never could, including the pasture planner roster.
  }

  async function transferSheep(newFlock: string) {
    if (!animal) return;
    // No-op short-circuit; the RPC also no-ops, but this avoids a round trip.
    if (newFlock === animal.flock) return;
    setNotice(null);
    const saveKey = `sheep-transfer:${animal.id}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      // Transactional RPC: updates the row, writes the sheep_transfers audit
      // row, and logs a status.changed Activity event atomically. No more
      // "moved but audit failed" partial state.
      await transferSheepAnimal(sb, animal.id, newFlock, authState && authState.name ? authState.name : null);
      await loadAll();
      reportSaved(saveKey, saveAttempt);
    } catch (caught) {
      const e = caught as ErrorLike;
      const msg = 'Transfer failed: ' + (e.message || String(e));
      setNotice({kind: 'error', message: msg});
      reportSaveFailed(saveKey, () => void transferSheep(newFlock), saveAttempt);
    }
  }

  async function addLambingRecord(sheepRecord: SheepAnimalRow, formData: LambingFormData) {
    setNotice(null);
    if (!formData.lambing_date) {
      setNotice({kind: 'error', message: 'Lambing date required.'});
      return false;
    }
    const id = String(Date.now()) + Math.random().toString(36).slice(2, 6);
    const rec = {
      id,
      dam_tag: sheepRecord.tag,
      lambing_date: formData.lambing_date,
      total_born: parseInt(formData.total_born) || 0,
      deaths: parseInt(formData.deaths) || 0,
      complications_flag: !!formData.complications_flag,
      complications_desc: formData.complications_desc || null,
      notes: formData.notes || null,
    };
    try {
      await createLambing.run({damSheepId: sheepRecord.id, record: rec});
    } catch (err) {
      setNotice({kind: 'error', message: 'Save failed: ' + writeErrorMessage(err)});
      return false;
    }
    // Migration 244: public.sheep_animal_operation ('lambing.create') verifies
    // the dam is live and writes the sheep.animal record.created Activity event
    // and receipt in the same transaction as the sheep_lambing_records insert.
    // The `await loadAll()` that stood here is gone. `createLambing.run` awaits
    // the declared invalidation set — this page's resource and the flocks index
    // — where loadAll refreshed only this one.
    return true;
  }

  async function deleteLambingRecord(recId: string) {
    if (!window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete this lambing record?', async () => {
      try {
        await deleteSheepLambingRecord(sb, recId, authState && authState.name ? authState.name : null);
        await loadAll();
      } catch (caught) {
        const e = caught as ErrorLike;
        setNotice({kind: 'error', message: 'Delete failed: ' + (e.message || String(e))});
      }
    });
  }

  async function deleteSheep() {
    if (!animal || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete(
      'Delete this sheep record? Admin/backend recovery is available, but this record will be hidden from active sheep views.',
      async () => {
        try {
          await softDeleteSheepAnimal(sb, animal.id, animal.tag || animal.id);
          navigate('/sheep/flocks');
        } catch (caught) {
          const e = caught as ErrorLike;
          setNotice({kind: 'error', message: 'Delete failed: ' + (e.message || String(e))});
        }
      },
    );
  }

  function navigateToSheep(target: {[column: string]: unknown} | null | undefined) {
    // `!animal` joins the guard this handler already had. It is unreachable —
    // SheepDetail, the only caller, renders below the `if (!animal)` early
    // return — and it is what narrows `animal` for the route state below.
    if (!target || !target.id || !animal) return;
    navigate('/sheep/flocks/' + target.id, {
      state: {fromSheepId: animal.id, fromSheepTag: animal.tag || animal.id},
    });
  }

  // Prev/Next within the originating list sequence — carry the sequence
  // forward so the neighbor record keeps its controls.
  function navigateSeq(id: RecordSeqId) {
    navigate('/sheep/flocks/' + id, recordSeqNavOptions(recordSeq));
  }

  function sheepTagSet(s: SheepAnimalRow | null): Set<string | null> {
    const set = new Set<string | null>();
    if (s && s.tag) set.add(s.tag);
    if (s && Array.isArray(s.old_tags)) {
      for (const ot of s.old_tags) {
        if (!ot || !ot.tag) continue;
        if (ot.source === 'import') continue;
        set.add(ot.tag);
      }
    }
    return set;
  }

  if (loading) {
    return <RecordPageLoading Header={Header} />;
  }

  if (loadError) {
    return (
      <RecordPageLoadError
        backLabel="Back to Flocks"
        onBack={() => navigate('/sheep/flocks')}
        notice={loadError}
        onRetry={loadAll}
        data-sheep-animal-load-error="true"
      />
    );
  }

  if (!animal) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Flocks"
        onBack={() => navigate('/sheep/flocks')}
        message="Sheep record not found. It may have been deleted."
      />
    );
  }

  const animalWeighIns = weighIns.filter((w) => {
    const tags = sheepTagSet(animal);
    return tags.has(w.tag);
  });

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody data-sheep-animal-page="1">
        <RecordBackLink label="Back to Flocks" onBack={() => navigate('/sheep/flocks')} />

        <RecordSequenceNav seq={recordSeq} currentId={sheepId} onNavigate={navigateSeq} />

        <RecordTitle>{animal.tag ? '#' + animal.tag : 'Untagged animal'}</RecordTitle>

        {notice && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}

        <SheepDetail
          key={animal.id}
          sheep={animal}
          ageLabel={age(animal.birth_date) || '—'}
          weighIns={animalWeighIns}
          lambing={lambingRecs.filter((r) => r.dam_tag === animal.tag)}
          lambs={allSheep.filter((x) => x.dam_tag === animal.tag)}
          dam={allSheep.find((x) => x.tag === animal.dam_tag)}
          sheepList={allSheep}
          fmt={fmt}
          FLOCKS={ALL_FLOCKS}
          FLOCK_LABELS={FLOCK_LABELS}
          onEdit={() => {}}
          onTransfer={(newFlock) => transferSheep(newFlock)}
          transferLocked={!canWriteLivestockRecords(authState as AccessAuthState)}
          onDelete={authState?.role === 'admin' ? () => deleteSheep() : undefined}
          onAddLambing={
            canWriteLivestockTables(authState as AccessAuthState) ? (data) => addLambingRecord(animal, data) : undefined
          }
          onDeleteLambing={
            canWriteLivestockRecords(authState as AccessAuthState) ? (id) => deleteLambingRecord(id) : undefined
          }
          onNavigateToSheep={(target) => navigateToSheep(target)}
          onNavigateBack={() => navigate('/sheep/flocks/' + fromSheepId)}
          canNavigateBack={Boolean(fromSheepId)}
          backToTag={fromSheepTag}
          readOnly={!canWriteLivestockTables(authState as AccessAuthState)}
          onPatch={canWriteLivestockTables(authState as AccessAuthState) ? (fields) => patchSheep(fields) : undefined}
          onClose={() => navigate('/sheep/flocks')}
          originOpts={originOpts}
          breedOpts={breedOpts}
        />

        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="sheep.animal"
          entityId={animal.id}
          entityLabel={animal.tag || animal.id}
        />
      </RecordPageBody>
    </RecordPageFrame>
  );
}
